import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ButtonGroup, Dropdown } from "react-bootstrap";
import DropdownMenu from "react-bootstrap/esm/DropdownMenu";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { formatCurrency } from "../../lib/currency/currency";
import { Icon } from "../../components";
import { getSystemLocale } from "../../lib/locale/locale";
import { isNumber } from "lodash";
import { SpaceOffering } from "../../offering/api";
import { calculateDiscountPercentage } from "../../data/partner_offering";
import { useAnalytics } from "../../lib/analytics/analytics";

interface QuantityPickerV2Props {
  multiPasses: SpaceOffering[];
  value?: string;
  disabled?: boolean;
  showDiscountLabel?: boolean;
  onChange: (addOnID: string, spaceOfferingID: string) => void;
  currency?: string | null;
}

export function QuantityPickerV2(props: QuantityPickerV2Props) {
  const { fbt } = useI18n();
  const {
    value,
    multiPasses,
    onChange,
    currency,
    disabled,
    showDiscountLabel,
  } = props;

  const singlePass = multiPasses.find(
    (m) => m.addOnID === "dayoffice" || m.addOnID === "daypass"
  );

  const sortByOrder = multiPasses.every((x) => isNumber(x.sortOrder));
  const multiPassOptions = multiPasses
    .filter((p) => p.addOnID !== "dayoffice" && p.addOnID !== "daypass")
    .sort((a, b) =>
      sortByOrder ? a.sortOrder! - b.sortOrder! : a.price - b.price
    );
  const allOptions = singlePass
    ? [singlePass, ...multiPassOptions]
    : multiPassOptions;
  const options = allOptions.map((multiPass) => ({
    name: multiPass.displayName,
    price: multiPass.price,
    discountPercentage: calculateDiscountPercentage(multiPass, singlePass),
    numberOfEntries: multiPass.numberOfEntries,
    value: multiPass.addOnID,
  }));

  const analytics = useAnalytics();
  const [selectedQuantity, setSelectedQuantity] = useState<string>();
  const onSelectQuantity = useCallback(
    (value: any) => {
      const multiPass = multiPasses.find((mp) => mp.addOnID === value);
      if (multiPass) {
        setSelectedQuantity(multiPass.displayName);
        onChange(value, multiPass.id);
      }

      const option = options.find((op) => op.value === value);

      if (!option) {
        return;
      }

      analytics.event(
        "Quantity Select Click",
        {
          Quantity: option.numberOfEntries,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    },
    [analytics, multiPasses, onChange, options]
  );

  useEffect(() => {
    if (value) {
      const quantity = multiPasses.find((mp) => mp.addOnID === value);
      setSelectedQuantity(quantity?.displayName);
    } else {
      setSelectedQuantity("");
    }
  }, [value, setSelectedQuantity, options, multiPasses]);

  const onlyOneOption = useMemo(() => options.length === 1, [options]);
  const addOnId = options[0]?.value;

  useEffect(() => {
    if (selectedQuantity || value) return;
    if (!addOnId) return;

    if (onlyOneOption && !disabled) {
      onChange(addOnId, multiPasses[0]?.id);
    }
  }, [
    value,
    onlyOneOption,
    disabled,
    onChange,
    addOnId,
    selectedQuantity,
    multiPasses,
  ]);

  if (onlyOneOption) {
    return null;
  }

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        as={ButtonGroup}
        className={`checkout-box-picker row ${
          disabled ? "checkout-box-picker-disable" : ""
        }`}
        id={`quantity-picker-v2`}
        size="lg"
        onSelect={(value: any) => onSelectQuantity(value)}
      >
        <Dropdown.Toggle disabled={disabled}>
          <div data-testid="quantity-picker" className="row">
            <div className="col-10 checkout-box-picker-placeholder">
              <span className="row fw-bold text-uppercase checkout-box-picker-placeholder__label">
                {fbt("Quantity")}
              </span>
              <span className="row checkout-box-picker-selected">
                {selectedQuantity ? (
                  selectedQuantity
                ) : (
                  <span className="fw-lighter checkout-box-picker-selected__placeholder">
                    {fbt("Select quantity")}
                  </span>
                )}
              </span>
            </div>
            <div className="checkout-box-picker-icon">
              <Icon name="fi-chevron-down" size={24} />
            </div>
          </div>
        </Dropdown.Toggle>
        <DropdownMenu
          popperConfig={{
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  altAxis: true,
                },
              },
            ],
          }}
        >
          <Dropdown.Header>
            <div className="dropdown-header">
              <span className="row fw-bold text-uppercase">
                {fbt("Select quantity")}
              </span>
            </div>
          </Dropdown.Header>
          {options.map((opt, index) => (
            <>
              <Dropdown.Item
                as="button"
                eventKey={opt.value}
                active={opt.value === value}
              >
                <div className="d-flex quantity-picker-option px-1">
                  <span className="col-6 align-self-start name">
                    {opt.name}
                  </span>
                  <span className="col-3 align-self-center">
                    {opt.value !== singlePass?.addOnID &&
                      showDiscountLabel &&
                      opt.discountPercentage > 0 && (
                        <span className="badge bg-warning text-dark">
                          {`${fbt("Save")} ${opt.discountPercentage}%`}
                        </span>
                      )}
                  </span>
                  <span className="col-3 align-self-center price-view">
                    {formatCurrency(
                      opt.price,
                      currency || "USD",
                      getSystemLocale()
                    )}
                  </span>
                </div>
              </Dropdown.Item>
              {index !== options.length - 1 && <Dropdown.Divider />}
            </>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}
